import { useState } from "react";
import AppShell from "../components/Shell/AppShell";
import I from "../components/Icons";
import { pick } from "../lib/i18n";

const copy = {
  pl: {
    confirmReset: "Przywrócić domyślny prompt dla tego trybu?", title: "Prompty",
    subtitlePre: "Edytuj szablony promptów używane do generacji grafik. Używaj", subtitlePost: "jako placeholderów.",
    variableName: "nazwa_zmiennej", settings: "Ustawienia", clickToSelect: "Kliknij żeby zaznaczyć",
    saving: "Zapisuję…", saved: "Zapisano", save: "Zapisz", restoreDefault: "Przywróć domyślny", chars: "znaków",
  },
  en: {
    confirmReset: "Restore the default prompt for this mode?", title: "Prompts",
    subtitlePre: "Edit the prompt templates used for generating visuals. Use", subtitlePost: "as placeholders.",
    variableName: "variable_name", settings: "Settings", clickToSelect: "Click to select",
    saving: "Saving…", saved: "Saved", save: "Save", restoreDefault: "Restore default", chars: "chars",
  },
};

type Template = {
  mode: string;
  label: string;
  body: string;
  is_custom: boolean;
  placeholders: string[];
};

type Props = {
  templates: Template[];
  current_user: { name: string; email: string; role: string };
  products?: { id: number; name: string }[];
};

type TemplateEntry = { body: string; is_custom: boolean; saving: boolean; saved: boolean; open: boolean };
type TemplateStateMap = Record<string, TemplateEntry>;

const patch = (s: TemplateStateMap, mode: string, updates: Partial<TemplateEntry>): TemplateStateMap => ({
  ...s,
  [mode]: { ...s[mode]!, ...updates },
});

export default function PromptTemplates({ templates, current_user, products = [] }: Props) {
  const tr = pick(copy);
  const [state, setState] = useState<TemplateStateMap>(() =>
    Object.fromEntries(templates.map((t) => [t.mode, { body: t.body, is_custom: t.is_custom, saving: false, saved: false, open: false }]))
  );

  const initials = current_user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  const toggle = (mode: string) =>
    setState((s) => patch(s, mode, { open: !s[mode]!.open }));

  const setBody = (mode: string, body: string) =>
    setState((s) => patch(s, mode, { body, saved: false }));

  const save = async (mode: string) => {
    setState((s) => patch(s, mode, { saving: true }));
    try {
      const res = await fetch(`/prompt_templates/${mode}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "" },
        body: JSON.stringify({ body: state[mode]!.body }),
      });
      const json = await res.json();
      if (json.ok) {
        setState((s) => patch(s, mode, { saving: false, saved: true, is_custom: json.is_custom }));
      }
    } catch {
      setState((s) => patch(s, mode, { saving: false }));
    }
  };

  const reset = async (mode: string, defaultBody: string) => {
    if (!confirm(tr.confirmReset)) return;
    setState((s) => patch(s, mode, { saving: true }));
    try {
      const res = await fetch(`/prompt_templates/${mode}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "" },
        body: JSON.stringify({ body: "" }),
      });
      const json = await res.json();
      if (json.ok) {
        setState((s) => patch(s, mode, { saving: false, saved: true, is_custom: false, body: json.body }));
      }
    } catch {
      setState((s) => patch(s, mode, { saving: false }));
    }
  };

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page" style={{ maxWidth: 800 }}>
        <div className="page-hd">
          <div>
            <h2>{tr.title}</h2>
            <p>{tr.subtitlePre} <code style={{ fontSize: 12, background: "var(--bg-3)", padding: "1px 5px", borderRadius: 3 }}>{`{{${tr.variableName}}}`}</code> {tr.subtitlePost}</p>
          </div>
          <div className="right">
            <a href="/settings" className="btn btn-sm btn-ghost">{tr.settings}</a>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {templates.map((t) => {
            const s = state[t.mode]!;
            return (
              <div key={t.mode} className="panel">
                <button
                  type="button"
                  className="panel-hd"
                  style={{ width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "10px 14px" }}
                  onClick={() => toggle(t.mode)}
                >
                  <span style={{ flex: 1, fontWeight: 600, fontSize: 13 }}>{t.label}</span>
                  {s.is_custom && (
                    <span style={{ fontSize: 10, fontFamily: "monospace", color: "var(--accent-ink)", background: "var(--accent-soft)", padding: "1px 6px", borderRadius: 3 }}>
                      custom
                    </span>
                  )}
                  <span style={{ color: "var(--muted)", fontSize: 12 }}>{s.open ? "▲" : "▼"}</span>
                </button>

                {s.open && (
                  <div className="panel-body" style={{ borderTop: "1px solid var(--line)" }}>
                    <div style={{ marginBottom: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {t.placeholders.map((p) => (
                        <code
                          key={p}
                          style={{ fontSize: 11, background: "var(--bg-3)", color: "var(--muted)", padding: "1px 6px", borderRadius: 3, cursor: "pointer", userSelect: "all" }}
                          title={tr.clickToSelect}
                        >
                          {`{{${p}}}`}
                        </code>
                      ))}
                    </div>

                    <textarea
                      value={s.body}
                      onChange={(e) => setBody(t.mode, e.target.value)}
                      className="input"
                      style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5, lineHeight: 1.6, minHeight: 320, resize: "vertical", whiteSpace: "pre" }}
                      spellCheck={false}
                    />

                    <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => save(t.mode)}
                        disabled={s.saving}
                      >
                        {s.saving ? tr.saving : s.saved ? `${I.check} ${tr.saved}` : tr.save}
                      </button>
                      {s.is_custom && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => reset(t.mode, t.body)}
                          disabled={s.saving}
                        >
                          {tr.restoreDefault}
                        </button>
                      )}
                      <span style={{ fontSize: 11, color: "var(--muted)", marginLeft: "auto" }}>
                        {s.body.length} {tr.chars}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
